import type { PreviewUpstreamCapabilityMutationResponse } from '@nadoutong/contracts';
import { useT } from '../../i18n/index.ts';
import { Button, Chip, Dialog } from '../../ui/index.ts';

export interface PendingCapabilityMutation {
  readonly action: 'ignore' | 'unignore';
  readonly capabilityIds: readonly string[];
  readonly preview: PreviewUpstreamCapabilityMutationResponse;
}

export function CapabilityImpactDialog({
  pending,
  busy,
  onClose,
  onConfirm,
}: {
  readonly pending: PendingCapabilityMutation | null;
  readonly busy: boolean;
  readonly onClose: () => void;
  readonly onConfirm: () => void;
}): JSX.Element {
  const t = useT();
  const impact = pending?.preview.impact;
  const impactTruncated = impact?.truncated.sku_targets === true;
  const ignoreProtected = pending?.action === 'ignore' &&
    (pending.preview.protected || impactTruncated);
  const scopeItems = [
    { scope: 'default' as const, label: t('upstream_detail.routing.impact.scope.default') },
    { scope: 'config' as const, label: t('upstream_detail.routing.impact.scope.config') },
    { scope: 'org' as const, label: t('upstream_detail.routing.impact.scope.org') },
    { scope: 'org_override' as const, label: t('upstream_detail.routing.impact.scope.org_override') },
  ];

  return (
    <Dialog
      open={pending !== null}
      onOpenChange={(open) => {
        if (!open && !busy) onClose();
      }}
      title={pending?.action === 'ignore'
        ? t('upstream_detail.routing.impact.ignore.title')
        : t('upstream_detail.routing.impact.unignore.title')}
      description={t('upstream_detail.routing.impact.subtitle')}
      size="lg"
      closeLabel={t('common.close')}
      testId="capability-impact-dialog"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={busy}>
            {t('common.cancel')}
          </Button>
          <Button
            variant={pending?.action === 'ignore' ? 'danger' : 'primary'}
            onClick={onConfirm}
            disabled={busy || pending === null || ignoreProtected}
            loading={busy}
            data-testid="capability-impact-confirm"
          >
            {pending?.action === 'ignore'
              ? t('upstream_detail.routing.impact.confirm_ignore')
              : t('upstream_detail.routing.impact.confirm_unignore')}
          </Button>
        </>
      }
    >
      {pending !== null ? (
        <div className="flex flex-col gap-4">
          <section className="rounded-lg bg-well p-3">
            <h4 className="m-0 text-label font-medium text-paper-mid">{t('upstream_detail.routing.impact.exact_ids')}</h4>
            <div className="mt-2 flex flex-wrap gap-1.5">
              {pending.capabilityIds.map((id) => (
                <Chip key={id} tone="neutral" lamp={false} className="font-mono">{id}</Chip>
              ))}
            </div>
          </section>
          {ignoreProtected ? (
            <p className="m-0 rounded-lg border border-bad-line bg-bad-dim px-3 py-2 text-dense text-bad-bright" role="alert">
              {impactTruncated
                ? t('upstream_detail.routing.impact.truncated_protected', {
                    skuShown: impact?.sku_targets.length ?? 0,
                    skuTotal: impact?.totals.sku_targets ?? 0,
                  })
                : t('upstream_detail.routing.impact.protected')}
            </p>
          ) : (
            <p className="m-0 rounded-lg border border-ok-line bg-ok-dim px-3 py-2 text-dense text-ok">
              {t('upstream_detail.routing.impact.safe')}
            </p>
          )}
          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
            {scopeItems.map((item) => (
              <div key={item.scope} className="rounded-lg border border-line-soft bg-card p-3">
                <span className="text-label text-paper-low">{item.label}</span>
                <strong className="mt-1 block font-mono text-title text-paper">
                  {impact?.totals.scope_counts[item.scope] ?? 0}
                </strong>
              </div>
            ))}
          </div>
          {impact !== undefined && impact.sku_targets.length > 0 ? (
            <section>
              <p className="mb-2 mt-0 text-label text-paper-low">
                {t('upstream_detail.routing.impact.examples', {
                  shown: impact.sku_targets.length,
                  total: impact.totals.sku_targets,
                })}
              </p>
              <ul className="m-0 max-h-48 overflow-y-auto rounded-lg bg-well p-3 pl-8 font-mono text-micro text-paper-mid">
                {impact.sku_targets.map((row) => (
                  <li key={`${row.scope}:${row.target_id}:${row.capability_id}`}>
                    {row.scope} · {row.target_id} · {row.sku_id} · {row.capability_id}
                  </li>
                ))}
              </ul>
            </section>
          ) : null}
        </div>
      ) : null}
    </Dialog>
  );
}
